import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { Loader2, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface PhoneOTPDialogProps {
  open: boolean;
  phone: string;
  onClose: () => void;
  onVerified: (verificationId: string) => void;
}

const RESEND_SECONDS = 60;

export function PhoneOTPDialog({ open, phone, onClose, onVerified }: PhoneOTPDialogProps) {
  const [otp, setOtp] = useState("");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const sentForPhone = useRef<string | null>(null);

  const sendOtp = async () => {
    setSending(true);
    setOtp("");
    setDevOtp(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-phone-otp", {
        body: { phone },
      });
      if (error || !data?.verification_id) {
        throw new Error(data?.error || error?.message || "Không gửi được OTP");
      }
      setVerificationId(data.verification_id);
      setCountdown(RESEND_SECONDS);
      if (data.dev_mode && data.dev_otp) {
        setDevOtp(data.dev_otp);
      }
      toast({ title: "Đã gửi mã OTP", description: `Mã đã được gửi tới ${phone}` });
    } catch (e: any) {
      toast({ title: "Lỗi", description: e.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  // Auto-send when dialog opens for a new phone
  useEffect(() => {
    if (open && phone && sentForPhone.current !== phone) {
      sentForPhone.current = phone;
      sendOtp();
    }
    if (!open) {
      sentForPhone.current = null;
      setOtp("");
      setVerificationId(null);
      setDevOtp(null);
      setCountdown(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleVerify = async (code: string) => {
    if (!verificationId || code.length !== 6) return;
    setVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-phone-otp", {
        body: { verification_id: verificationId, otp: code, phone },
      });
      if (error || !data?.verified) {
        throw new Error(data?.error || error?.message || "Mã OTP không đúng");
      }
      toast({ title: "Xác thực thành công" });
      onVerified(verificationId);
    } catch (e: any) {
      toast({ title: "Lỗi", description: e.message, variant: "destructive" });
      setOtp("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Xác thực số điện thoại
          </DialogTitle>
          <DialogDescription>
            Nhập mã OTP 6 số đã gửi tới <strong>{phone}</strong> qua Zalo
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-4">
          {devOtp && (
            <div className="w-full rounded-lg border border-dashed border-warning bg-warning/10 p-3 text-center text-sm">
              <div className="font-semibold text-warning">DEV MODE — Zalo chưa kết nối</div>
              <div>OTP: <span className="font-mono text-lg font-bold">{devOtp}</span></div>
            </div>
          )}

          <InputOTP
            maxLength={6}
            value={otp}
            onChange={(v) => {
              setOtp(v);
              if (v.length === 6) handleVerify(v);
            }}
            disabled={verifying || sending}
          >
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>

          {verifying && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Đang kiểm tra...
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            {countdown > 0 ? (
              <>Gửi lại mã sau {countdown}s</>
            ) : (
              <Button variant="link" size="sm" disabled={sending} onClick={sendOtp}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gửi lại mã"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
